// LIBRERIE/IMPORTS
import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import he from "he";

// COMPONENTI
import Pagination from "./Pagination";
import PropertySearchField from "../Home/Search";
// import WidgetNetwork from "./Widgets/WidgetNetwork";
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import Breadcrumb from "./Breadcrumb";
import ImmobiliGrid from "./ImmobiliGrid";
import ImmobiliList from "./ImmobiliList";

// HOOKS
import { useGetLocationTree, FullProvince } from "../../hooks/standard/useGetLocationTree";
import { getIdFromSlug } from '../../config/mappings';
import { useGetProperties } from "../../hooks/useGetProperties";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";
import { useCanonical } from "../../hooks/useCanonical";

// CONFIGURAZIONI
import { AGENCY_ID, IMMOBILI_SHOW_VIEW_TOGGLE, appConfig } from "../../config/appConfig";

// FOGLI DI STILE
import styles from './Immobili.module.css';
import formStyle from "../../assets/jsStyle/formStyle";

const Immobili: React.FC = () => {

    const [currentPage, setCurrentPage] = useState(1);
    const [propertiesPerPage] = useState(9);
    const [orderBy, setOrderBy] = useState<string>("insertionDate,desc");
    const [currentView, setCurrentView] = useState<'grid' | 'list'>(appConfig.ui.immobiliPage.defaultView);
    const location = useLocation();
    const { t } = useTranslation();

    const params = useParams();
    const [searchParams] = useSearchParams();

    const { locationTree, loadingLocations } = useGetLocationTree();

    const memoizedFilters = useMemo(() => {
        // Se i dati delle località stanno caricando, non posso parsare l'URL in modo affidabile. Attendiamo.
        if (loadingLocations) {
            return null;
        }

        const path = params['*']; // Es. "vendita/appartamento/livorno/caletta"
        const segments = path ? path.split('/').filter(Boolean) : [];
        let segmentIndex = 0;

        const filters: any = {
            contract: [],
            type: [],
            provincia: '',
            comune: [],
            frazione: [],
            quartiere: [],
        };

        // --- 1. PARSING DEL PERCORSO URL "INTELLIGENTE" ---

        // Step 1: Provo a mappare il segmento corrente come CONTRATTO
        if (segments[segmentIndex]) {
            const contractId = getIdFromSlug('contratto', segments[segmentIndex]);
            if (contractId) {
                filters.contract.push(contractId);
                segmentIndex++; // Se matcha, passa al segmento successivo
            }
        }
        // Step 1-a: Provo a mappare il segmento corrente come CONTRATTO - (nel caso in cui l' URL avesse query string))
        if (searchParams.has('contratto')) {
            const searchContractParams = searchParams.getAll("contratto");
            filters.contract.push(searchContractParams);
        }

        // Step 2: Provo a mappare il segmento corrente come TIPOLOGIA
        if (segments[segmentIndex]) {
            const typeId = getIdFromSlug('tipologia', segments[segmentIndex]);
            if (typeId) {
                filters.type.push(typeId);
                segmentIndex++;
            }
        }
        // Step 2-a: Provo a mappare il segmento corrente come tipologia - (nel caso in cui l' URL avesse query string))
        if (searchParams.has('tipologia')) {
            const searchTypeParams = searchParams.getAll("tipologia");
            filters.type.push(searchTypeParams);
        }

        // Step 3: Provo a mappare i segmenti rimanenti come LOCALITÀ (in gerarchia)
        let currentLocationScope: any = locationTree; // Inizio cercando tra le province
        let provinceFound: FullProvince | undefined;
        let comuneFound: FullProvince['comuni'][0] | undefined;

        // Cerco la PROVINCIA
        if (segments[segmentIndex] && currentLocationScope) {
            provinceFound = currentLocationScope.find((p: FullProvince) => p.slug === segments[segmentIndex]);
            if (provinceFound) {
                filters.provincia = provinceFound.codiceIstat;
                currentLocationScope = provinceFound.comuni;
                segmentIndex++;
            }
        }

        // Cerco il COMUNE
        if (segments[segmentIndex] && currentLocationScope) {
            comuneFound = currentLocationScope.find((c: any) => c.slug === segments[segmentIndex]);
            if (comuneFound) {
                filters.comune = [comuneFound.codiceIstat];
                currentLocationScope = comuneFound;
                segmentIndex++;
            }
        }
        // Step 3-a: Provo a mappare il segmento corrente come comune - (nel caso in cui l' URL avesse query string))
        if (searchParams.has('comune')) {
            const searchTypeParams = searchParams.getAll("comune");
            filters.comune.push(...searchTypeParams);
        }

        // Cerco la FRAZIONE o il QUARTIERE
        if (segments[segmentIndex] && currentLocationScope) {
            const frazioneFound = currentLocationScope.frazioni?.find((f: any) => f.slug === segments[segmentIndex]);
            if (frazioneFound) {
                filters.frazione = [frazioneFound.id];
                segmentIndex++;
            } else {
                const quartiereFound = currentLocationScope.quartieri?.find((q: any) => q.slug === segments[segmentIndex]);
                if (quartiereFound) {
                    filters.quartiere = [quartiereFound.id];
                    segmentIndex++;
                }
            }
        }

        // --- 2. GESTIONE DEI FILTRI DINAMICI DALLA QUERY STRING ---
        const queryParams = new URLSearchParams(location.search);
        queryParams.forEach((value, key) => {
            switch (key) {
                case 'locali_min': filters.minPremises = Number(value); break;
                case 'locali_max': filters.maxPremises = Number(value); break;
                case 'camere_min': filters.minRooms = Number(value); break;
                case 'camere_max': filters.maxRooms = Number(value); break;
                case 'bagni_min': filters.minBathrooms = Number(value); break;
                case 'bagni_max': filters.maxBathrooms = Number(value); break;
                case 'prezzo_min': filters.minPrice = Number(value); break;
                case 'prezzo_max': filters.maxPrice = Number(value); break;
                case 'mq_min': filters.sqmtMin = Number(value); break;
                case 'mq_max': filters.sqmtMax = Number(value); break;
                case 'giardino_privato': filters.gardenPrivate = Boolean(value); break;
                case 'piscina': filters.swimmingPool = Boolean(value); break;
                case 'terrazzo': filters.terrace = Boolean(value); break;
                case 'balcone': filters.balcony = Boolean(value); break;
                case 'garage': filters.garage = Boolean(value); break;
                case 'postoAuto': filters.carPlaces = Boolean(value); break;
                case 'ascensore': filters.elevator = Boolean(value); break;
                case 'nuova_costruzione': filters.isNewlyBuilt = Boolean(value); break;
                case 'riferimento': filters.ref = String(value); break;
            }
        });

        return filters;

    }, [params, location.search, locationTree, loadingLocations]);

    {/*Hook x chiamata immobili - l' ultimo parametro è lo skip, finchè è false l'hook non parte */ }
    const { properties, totalCount, loading, error } = useGetProperties(AGENCY_ID, {
        limit: 100000000,
        offset: 0,
        withCount: true,
        filters: memoizedFilters || {},
        orderBy: {
            field: orderBy.split(",")[0] || 'insertionDate',
            direction: (orderBy.split(",")[1] as 'desc' | 'asc') || 'desc',
        },
    }, loadingLocations);

    const options = [
        { value: "insertionDate,desc", label: t('propertyList.sortOptions.dateDesc') },
        { value: "insertionDate,asc", label: t('propertyList.sortOptions.dateAsc') },
        { value: "price,desc", label: t('propertyList.sortOptions.priceDesc') },
        { value: "price,asc", label: t('propertyList.sortOptions.priceAsc') },
    ];

    const isLoading = loading || (params.contrattoSlug && loadingLocations);

    const totalPages = totalCount > 0 ? Math.ceil(totalCount / propertiesPerPage) : Math.ceil(properties.length / propertiesPerPage);
    const startIndex = (currentPage - 1) * propertiesPerPage;
    const currentProperties = useMemo(() => {
        if (!isLoading && !error) {
            return properties.slice(startIndex, startIndex + propertiesPerPage);
        }
        return [];
    }, [properties, propertiesPerPage, isLoading, error, startIndex]);


    const { agency } = useGetAgencyDetails(AGENCY_ID);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo(0, 0);
        }
    };

    const ViewComponent = currentView === 'list' ? ImmobiliList : ImmobiliGrid;
    const pageTitle = t('propertyList.pageTitle');
    const agencyName = agency?.agencyName ?? t('common.defaultAgencyName');
    const helmetTitle = t('common.pageTitlePattern', { pageTitle: pageTitle, agencyName: he.decode(`${agencyName}`) });

    const canonicalUrl = useCanonical();

    return (
        <>
            <Helmet>
                <title>{he.decode(`${helmetTitle}`)}</title>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            <div className="position-relative">
                <SubHeaderDefault title={t('propertyList.pageTitle')} isSeo={true} />

                {appConfig.features.breadcrumbs.enabled && !loadingLocations && (
                    <div className="breadcrumb-wrapper">
                        <div className="container-fluid">
                            <Breadcrumb
                                filters={memoizedFilters}
                                locationTree={locationTree}
                            />
                        </div>
                    </div>
                )}
            </div>

            <section id="search-box" className="py-3 bg-cuge-grill">
                <div className="container-fluid w-90">
                    <h2 className="pl-3 text-center" dangerouslySetInnerHTML={{ __html: t('propertyList.title') as string }} />
                    <div className="row">
                        <div className="col-lg-12 mb-4">
                            <PropertySearchField initialFilters={memoizedFilters} />
                        </div>
                    </div>
                </div>
            </section>

            <section id="properties-box" className="space-ptb">
                <div className="container-fluid w-90">
                    <div className="row align-items-center mb-4 mb-lg-5">
                        <div className="col-auto">
                            <h3 className="mb-2 mb-sm-0">{isLoading ? t('common.loading') : `${totalCount} ${t('propertyList.foundLabel')}`}</h3>
                        </div>
                        <div className="col">
                            <div className="property-filter d-sm-flex align-items-center justify-content-between flex-wrap">
                                {/* ... Dropdown Ordinamento ... */}
                                <ul className="property-short list-unstyled d-sm-flex mb-0 align-items-center">
                                    <li>
                                        <form className="form-inline">
                                            <div className="form-group d-md-flex d-block align-items-center mb-0">
                                                <label className="justify-content-start mr-3">{t('common.orderBy')}</label>
                                                <div className="short-by">
                                                    <Select
                                                        id="alternativeOrder"
                                                        value={options.find((option) => option.value === orderBy)}
                                                        options={options}
                                                        onChange={(e) => setOrderBy(e?.value as string)}
                                                        isSearchable={false}
                                                        styles={formStyle}
                                                        placeholder={t('common.select')}
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </li>
                                </ul>

                                {IMMOBILI_SHOW_VIEW_TOGGLE && (
                                    <div className="property-view-toggle pr-2">
                                        <button
                                            type="button"
                                            className={`${styles.viewToggleButton} ${currentView === 'grid' ? styles.active : ''}`}
                                            onClick={() => setCurrentView('grid')}
                                            aria-label={t('propertyList.viewToggle.gridLabel') || 'Visualizzazione Griglia'}
                                            aria-pressed={currentView === 'grid'}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-th" aria-hidden="true"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.viewToggleButton} ${currentView === 'list' ? styles.active : ''}`}
                                            onClick={() => setCurrentView('list')}
                                            aria-label={t('propertyList.viewToggle.listLabel') || 'Visualizzazione Lista'}
                                            aria-pressed={currentView === 'list'}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-list" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">

                        {/* Risultati */}
                        <div className="col-lg-12">

                            {!loadingLocations && error && (
                                <div className="alert alert-danger">{error || t('propertyList.errorLoadingProperties')}</div>
                            )}

                            {!loadingLocations && !error && (
                                <div className="row">
                                    <ViewComponent
                                        properties={currentProperties}
                                        loading={loading}
                                        error={null}
                                        basePath="immobile"
                                        labelSold={false}
                                    />
                                </div>
                            )}

                            {/* Paginazione */}
                            {totalCount > propertiesPerPage && !error && (
                                <div className="row mt-4 px-3">
                                    <div className="col-12">
                                        <Pagination
                                            count={totalCount || 0}
                                            limit={propertiesPerPage}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            { /*
            <section className="space-ptb">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <WidgetNetwork />
                        </div>
                    </div>
                </div>
            </section>
            */}
        </>
    );
};

export default Immobili;